import * as Sentry from '@sentry/browser'
import livewireComponentId from '../global/livewireComponentId.js'
import { enqueueSerializedRequest } from '../global/serializedRequestQueue.js'

/**
 * Distribution-circle picker for the roster editor: chips + a filtering search
 * input over grouped team/person options. Every chip change auto-saves the
 * circle (debounced) — there is deliberately no separate save button, it was
 * too easy to hit while picking people.
 *
 * The selection lives in LOCAL Alpine state (`selected`) and is sent to the
 * server as a saveCircle() PARAMETER. Never read or write `$wire.circleKeys`
 * from component code: Alpine's reactive proxy layered over Livewire's $wire
 * proxy silently swallows property writes and even breaks array iteration.
 */
// Every picker instance gets a number, so a broadcast can be told apart from the
// echo of the picker that sent it.
let pickerInstances = 0

export default ({
  teamOptions = {},
  personOptions = {},
  selectedLabels = {},
  selected = [],
  errorTitle = 'Error',
  errorMessage = 'Save failed',
  // Livewire method that receives the full selection as its parameter —
  // the picker is reused outside the roster editor (e.g. event participants)
  saveMethod = 'saveCircle',
  // Leading arguments handed to the save method before the selection — a picker
  // that belongs to one row of a list (e.g. an agenda point) identifies it here.
  saveArgs = [],
  // Poll drafts opt in to a page-level queue because configuration and audience
  // writes share one optimistic-lock version. Other picker consumers retain
  // their existing independent queue.
  requestQueue = null,
  // Optional event used by a parent workflow to flush this picker's debounce
  // before a dependent action such as publishing.
  flushEvent = null,
  // Optional Livewire property name that mirrors the selection IMMEDIATELY as
  // a deferred update (wire.set(..., false): no request of its own, rides
  // along with the next one). Closes the debounce race: adding a chip and
  // hitting the form's submit button within the 500 ms debounce would
  // otherwise submit with a stale server-side selection.
  syncProperty = null,
  // Staged pickers only mirror local selection into Livewire. They never
  // debounce a persistence request or claim that the selection was saved.
  mode = 'autosave',
  // Optional identity of the selection this picker edits. Two pickers that carry
  // the same key are two views of ONE selection - the meeting editor shows an
  // agenda point in the list and on the beamer stage at the same time. Whichever
  // of them is used broadcasts the new selection; the other adopts it instead of
  // holding on to what it was rendered with and later saving that stale set.
  syncKey = null
}) => {
  // Only the component ID (a string) lives in the closure — the $wire proxy
  // itself is re-fetched per use and NEVER stored (in reactive state it would
  // get wrapped by Alpine's reactive proxy, which breaks its method resolution;
  // the same double-proxy trap as reading $wire.circleKeys, see header).
  let wireId = null
  let stopFlushListening = null
  // Tells a broadcast of this very picker apart from its twin's.
  const instanceId = ++pickerInstances

  return {
    teamOptions,
    personOptions,
    selectedLabels,
    // PHP arrays with non-sequential keys serialize as objects — normalize
    selected: Array.isArray(selected) ? selected : Object.values(selected ?? {}),
    errorTitle,
    errorMessage,
    search: '',
    open: false,
    // Dropdown nach oben öffnen, wenn unterhalb des Inputs kein Platz mehr ist
    dropUp: false,
    highlight: 0,
    ft: [],
    fp: [],
    saving: false,
    savedFlash: false,
    _saveTimer: null,
    _flashTimer: null,
    _saveGeneration: 0,
    _requestFailed: false,

    /**
     * Resolve the owning Livewire component's $wire, fresh on every use.
     *
     * Never keep using `this.$wire` directly: Alpine resolves magics through
     * the element of the TRIGGERING expression — chips/options are removed
     * from the DOM by the very mutation they trigger, and by the time the
     * debounced timer fires, $wire on that dead element degrades to Livewire's
     * noop fallback and the save silently evaporates. The component ID is read
     * from the stable x-data root in init(); Livewire.find() (which returns
     * the $wire object directly) then works at any later moment — even when
     * Livewire hadn't booted this component yet at interaction time.
     */
    /**
     * Öffnet die Liste und entscheidet die Aufklapp-Richtung: nach oben, wenn
     * unter dem Input weniger Platz ist als die Liste braucht (max-h-64 + Rand)
     * und oberhalb genug vorhanden wäre.
     */
    openList() {
      this.open = true
      this.updateDirection()
    },

    closeListOnEscape(event) {
      if (!this.open) return false
      this.open = false
      event.preventDefault()
      event.stopPropagation()
      return true
    },

    /**
     * Aufklapp-Richtung bei JEDEM Öffnen neu bestimmen (Scroll-Position ändert
     * sich): nach oben, wenn unter dem Input weniger Platz ist als die Liste
     * braucht (max-h-64 + Rand ≈ 272px) und oben mehr wäre. Anker über $refs,
     * gegen window.innerHeight (Viewport, nicht Scroll-Container).
     */
    updateDirection() {
      const anchor = this.$refs?.search
      const rect = anchor?.getBoundingClientRect?.()
      if (!rect) return

      const needed = 272 // max-h-64 (256px) + Abstand
      const below = window.innerHeight - rect.bottom
      this.dropUp = below < needed && rect.top > below
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    init() {
      // Teleport aware: a picker can stand on the meeting editor's beamer stage,
      // which hangs on the body - `closest()` alone finds no component there.
      wireId = livewireComponentId(this.$root)
      this.recompute()
      this.$watch('search', () => {
        this.highlight = 0
        this.recompute()
      })

      if (flushEvent) {
        const onFlush = event => {
          if (event.detail?.queueKey !== requestQueue) return
          if (!this._saveTimer && !this.saving && !this._requestFailed) return

          if (event.detail?.probeOnly) {
            event.detail?.markPending?.()

            return
          }

          event.detail?.waitUntil?.(this.flush())
        }

        window.addEventListener(flushEvent, onFlush)
        stopFlushListening = () => window.removeEventListener(flushEvent, onFlush)
      }
    },

    recompute() {
      const term = this.search.toLowerCase()
      this.ft = Object.entries(this.teamOptions)
        .filter(([key, label]) => !this.selected.includes(key) && label.toLowerCase().includes(term))
      this.fp = Object.entries(this.personOptions)
        .filter(([key, label]) => !this.selected.includes(key) && label.toLowerCase().includes(term))
    },

    flat() {
      return [...this.ft, ...this.fp]
    },

    labelFor(key) {
      return this.teamOptions[key] ?? this.personOptions[key] ?? this.selectedLabels[key] ?? key
    },

    add(key) {
      if (key && !this.selected.includes(key)) {
        this.selected = [...this.selected, key]
        this.queueSave()
      }
      this.recompute()
    },

    addAll(map) {
      const keys = Object.keys(map).filter((key) => !this.selected.includes(key))
      if (keys.length) {
        this.selected = [...this.selected, ...keys]
        this.queueSave()
      }
      this.recompute()
    },

    remove(key) {
      this.selected = this.selected.filter((selectedKey) => selectedKey !== key)
      this.queueSave()
      this.recompute()
    },

    move(dir) {
      const count = this.flat().length
      if (!count) return
      this.open = true
      this.updateDirection()
      this.highlight = (this.highlight + dir + count) % count
    },

    choose() {
      const flat = this.flat()
      if (flat[this.highlight]) {
        this.add(flat[this.highlight][0])
        this.search = ''
        this.highlight = 0
      }
    },

    queueSave() {
      this.broadcastSelection()

      // Mirror the selection into the Livewire property right away (deferred,
      // no own request) so a form submit inside the debounce window already
      // carries the current selection — the debounced call below stays the
      // auto-save path.
      if (syncProperty) {
        const wire = this._livewire()
        if (wire) wire.set(syncProperty, [...this.selected], false)
      }

      if (mode === 'staged') {
        this.saving = false
        this.savedFlash = false
        this._requestFailed = false

        return
      }

      // generation counter: an older save finishing while a newer change is
      // already queued/in flight must not flash "saved" (or clear "saving") —
      // only the save that covers the LATEST selection may report success
      const generation = ++this._saveGeneration
      this.saving = true
      this.savedFlash = false
      this._requestFailed = false
      clearTimeout(this._saveTimer)
      this._saveTimer = setTimeout(() => {
        this._saveTimer = null
        this._send(generation)
      }, 500)
    },

    _send(generation) {
      // Poll drafts put audience and configuration requests into one shared
      // queue. Every other picker keeps the established per-instance chain.
      this._saveChain = requestQueue
        ? enqueueSerializedRequest(requestQueue, () => this._performSave(generation))
        : (this._saveChain ?? Promise.resolve()).then(() => this._performSave(generation))

      return this._saveChain
    },

    flush() {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer)
        this._saveTimer = null

        return this._send(this._saveGeneration)
      }

      if (requestQueue && this._requestFailed) {
        this.saving = true

        return this._send(this._saveGeneration)
      }

      return this._saveChain ?? Promise.resolve(true)
    },

    /**
     * Tell the twin of this picker what is now selected. Only the selection
     * travels: both pickers were rendered from the same options, so the labels
     * resolve on the other side as well.
     */
    broadcastSelection() {
      if (!syncKey) return

      window.dispatchEvent(new CustomEvent('people-picker-synced', {
        detail: { key: syncKey, origin: instanceId, selected: [...this.selected] }
      }))
    },

    /**
     * Adopt what the twin picker stored. Deliberately without a save of its own:
     * the picker that was actually used is saving, and a second write of the same
     * set would only race it.
     */
    adoptPickerSelection(detail = {}) {
      if (!syncKey || detail.key !== syncKey || detail.origin === instanceId) return
      if (!Array.isArray(detail.selected)) return

      this.selected = [...detail.selected]
      if (detail.reset === true) {
        this.search = ''
        this.open = false
      }
      this.recompute()
    },

    async _performSave(generation) {
      try {
        const wire = this._livewire()
        if (!wire) throw new Error('rosterCirclePicker: Livewire component not resolvable')
        const result = await wire[saveMethod](...saveArgs, [...this.selected])

        // A refused save answers with an EXPLICIT false and has already reported
        // its reason (inline error + danger toast) — never flash "saved" on top
        // of it. Only false counts as a rejection: the roster and event editors
        // reuse this picker and their save methods return nothing at all.
        if (result === false) {
          if (generation === this._saveGeneration) this._requestFailed = false

          return false
        }

        // success of the LATEST generation only — a failed or superseded save
        // must never flash "saved"
        if (generation === this._saveGeneration) {
          this._requestFailed = false
          this.savedFlash = true
          clearTimeout(this._flashTimer)
          this._flashTimer = setTimeout(() => { this.savedFlash = false }, 2500)
        }

        return true
      } catch (error) {
        // no silent failing — but no rethrow either, a rejected chain would
        // block all future saves. Toast via window event (NOT this.$dispatch:
        // an element-bound dispatch from a removed element never bubbles).
        window.dispatchEvent(new CustomEvent('notify', { detail: { type: 'danger', title: this.errorTitle, message: this.errorMessage } }))
        Sentry.captureException(error)
        console.error(error)
        if (generation === this._saveGeneration) this._requestFailed = true

        return false
      } finally {
        if (generation === this._saveGeneration) this.saving = false
      }
    },

    destroy() {
      if (mode === 'staged') {
        clearTimeout(this._flashTimer)
        stopFlushListening?.()
        stopFlushListening = null

        return
      }

      if (this._saveTimer && !requestQueue) {
        clearTimeout(this._saveTimer)
        this._saveTimer = null
        this._saveChain = this._performSave(this._saveGeneration)
      } else {
        this.flush()
      }
      clearTimeout(this._flashTimer)
      stopFlushListening?.()
      stopFlushListening = null
    }
  }
}
